import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';

import { OAuthService } from 'angular-oauth2-oidc';
import { Store } from '@ngrx/store';

import { DISCOVERY_URL } from './auth.tokens';
import { ExtraauthService } from './extraauth.service';
import { FLAGS } from './models/mock-flags';
import * as LoginActions from '../shared/state-management/actions/login';
import * as RouterActions from '../shared/state-management/actions/router';
import { TranslateService } from '../shared/i18n/services/translate.service';
import { WindowRef } from '../shared/services/window-ref';
import { getLang, setLang } from '../shared/i18n/i18n-providers';
import { environment } from '../../environments/environment';
import { IpcChannelService } from '../shared/services/ipc-channel/ipc-channel.service';
import { IdentityClaims } from './models/identity-claims.model';

interface FormFields {
	userName: string;
	password: string;
	two_factor_code: string;
	two_factor_remember_me: boolean;
	two_factor_mail: string;
	mail_opt_in: boolean;
}


@Component({
	selector: 'go-suite-login',
	templateUrl: './login.component.html',
	styleUrls: ['./login.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginComponent implements OnInit {

	public errorMessage: string = '';
	public country: string = getLang(this._window.nativeWindow);
	public flags = FLAGS;
	public isLangOption: boolean = false;
	public showTwoFactorFields: boolean = false;
	public showQueryTwoFactorMailFields: boolean = false;

	public loginForm: FormGroup = this._builder.group({
		userName: ['', [Validators.required]],
		password: ['', [Validators.required]],
		two_factor_code: [''],
		two_factor_remember_me: [''],
		two_factor_mail: [''],
		mail_opt_in: [''],
	});

	private _target: string = '/';
	private _issuer: string = '';

	public constructor(
		private oauthService: OAuthService,
		private extraAuthService: ExtraauthService,
		@Inject(DISCOVERY_URL) private discoveryUrl: string,
		private _cd: ChangeDetectorRef,
		private store: Store<{}>,
		private route: ActivatedRoute,
		private _builder: FormBuilder,
		private _translateService: TranslateService,
		private _window: WindowRef,
		private _ipcService: IpcChannelService,
	) {}

	public ngOnInit(): void {
		this._target = this.route.snapshot.queryParams['target'] || '/';

		const hasIdToken = this.oauthService.hasValidIdToken();
		const hasAccessToken = this.oauthService.hasValidAccessToken();

		if (hasIdToken && hasAccessToken) {
			const claims = this.oauthService.getIdentityClaims();
			this.store.dispatch(new LoginActions.LoginSuccessful(claims as IdentityClaims));
			this.store.dispatch(new RouterActions.Go({path: [this._target]}));
		}
	}

	public onLoginXena({value, valid}: { value: FormFields, valid: boolean }) {
		this.loginForm.markAsTouched();

		if (this.checkLoginFormForErrors()) {
			return;
		}

		if (value && value.two_factor_mail) {
			this.enableTwoFactorAuth(this._issuer, value.two_factor_mail, value.mail_opt_in);
			return;
		}

		if (value && value.two_factor_code) {
			this.sendTwoFactorCode(this._issuer, value.two_factor_code, value.two_factor_remember_me);
			return;
		}

		this.oauthService.loadDiscoveryDocument(this.discoveryUrl).then((info: { info: {discoveryDocument: {issuer}}}) => {
			this._issuer =  info.info.discoveryDocument.issuer;
			this.oauthService.fetchTokenUsingPasswordFlow(value.userName, value.password).then((resp) => {
				if (resp && resp['error']) {
					if (resp['error'] === 'two_factor_required') {
						this._displayTwoFactorFormFields();
					} else if (resp['error'] === 'invalid_code') {
						throw new Error('invalid_code');
					} else if (resp['error'] === 'two_factor_email_required') {
						this._displayQueryTwoFactorMail();
					}
					return;
				}

				return this.processTokenResponse(resp);
			}).catch(() => this.loginFailure());
		});
	}

	public chooseLanguage(country: string) {
		this.country = country;
		this.isLangOption = false;
		setLang(this._window.nativeWindow, country);

		if (environment.production) {
			this._window.nativeWindow.location.href = '/';
		} else {
			this._window.nativeWindow.location.reload();
		}
	}

	public switchLanguage() {
		this.isLangOption = !this.isLangOption;
	}

	private checkLoginFormForErrors(): boolean {
		const twoFactorCodeControl = this.loginForm.get('two_factor_code');
		const twoFactorMailControl = this.loginForm.get('two_factor_mail');
		const mailOptInRequiredControl = this.loginForm.get('mail_opt_in');
		const passwordControl = this.loginForm.get('password');

		if (twoFactorCodeControl && twoFactorCodeControl.errors && twoFactorCodeControl.errors['required']) {
			this.loginFailure('Bitte Code eingeben');
			return true;
		}

		if (twoFactorMailControl && twoFactorMailControl.errors && (twoFactorMailControl.errors['required'] || twoFactorMailControl.errors['email'])) {
			if (mailOptInRequiredControl && mailOptInRequiredControl.errors && mailOptInRequiredControl.errors['required']) {
				this.loginFailure('Angaben unvollständig');
				return true;
			}

			this.loginFailure('Bitte E-Mail eingeben');
			return true;
		}

		if (mailOptInRequiredControl && mailOptInRequiredControl.errors && mailOptInRequiredControl.errors['required']) {
			this.loginFailure('Bitte dem Informationsversand zustimmen');
			return true;
		}

		if (passwordControl.value === '') {
			passwordControl.setErrors({'invalid': true});
			passwordControl.markAsTouched();
			return true;
		}

		return false;
	}

	private enableTwoFactorAuth(issuer: string, twoFactorMail: string, mailOptIn: boolean): void {
		this.extraAuthService.enableTwoFactor(issuer, twoFactorMail, mailOptIn)
			.subscribe(
				resp => {
					this.extraAuthService.storeAccessTokenResponse(resp['access_token'], resp['refresh_token'], resp['expires_in']);
					return this.processTokenResponse(resp);
				},
				() => this.loginFailure('Fehler bei der Aktivierung von Zwei-Faktor'));
	}

	private sendTwoFactorCode(issuer: string, twoFactorCode: string, rememberMe: boolean): void {
		this.extraAuthService.redeemTwoFactorCode(issuer, twoFactorCode, rememberMe)
			.subscribe(
				resp => {
					this.extraAuthService.storeAccessTokenResponse(resp['access_token'], resp['refresh_token'], resp['expires_in']);
					return this.processTokenResponse(resp);
				},
				() => this.loginFailure('Fehler beim Einlösen des Codes'));
	}

	private processTokenResponse(resp) {
		let refreshToken: string = '';
		let iss: string = '';
		let sub: string = '';
		let name: string = '';

		refreshToken = resp['refresh_token'];

		this.extraAuthService.processIdToken(
			resp['id_token'],
			resp['access_token'],
		).then(idToken => {
			this.extraAuthService.storeIdToken(idToken);
			iss = idToken.idTokenClaims['iss'];
			sub = idToken.idTokenClaims['sub'];
			name = idToken.idTokenClaims['name'];
		});

		// Loading data about the user
		return this.oauthService.loadUserProfile().then(() => {
			// Using the loaded user data
			const claims = this.oauthService.getIdentityClaims();

			this.errorMessage = '';
			this._cd.markForCheck();

			this._ipcService.send({
				action: 'login',
				tokenInfos: {
					refresh_token: refreshToken,
					iss,
					sub,
					name
				}
			}, 'login');

			this.store.dispatch(new LoginActions.LoginSuccessful(claims as IdentityClaims));
			this.store.dispatch(new RouterActions.Go({path: [this._target]}));

		});
	}

	private loginFailure(errorMessage: string = 'Login fehlerhaft') {
		this.errorMessage = this._translateService.translate(errorMessage);
		this._cd.markForCheck();
		this.store.dispatch(new LoginActions.LoginFailed());

		if (errorMessage === 'Login fehlerhaft') {
			this.loginForm.get('password').setErrors({'invalid': true});
		}
	}

	private _displayTwoFactorFormFields() {
		this.showTwoFactorFields = true;
		this.showQueryTwoFactorMailFields = false;
		this.loginForm.get('two_factor_code').setValidators([Validators.required]);
		this._cd.markForCheck();
	}

	private _displayQueryTwoFactorMail() {
		this.showQueryTwoFactorMailFields = true;
		this.showTwoFactorFields = false;
		this.loginForm.get('two_factor_mail').setValidators([Validators.required, Validators.email]);
		this.loginForm.get('mail_opt_in').setValidators([Validators.requiredTrue]);
		this._cd.markForCheck();
	}
}



// WEBPACK FOOTER //
// ./src/app/login/login.component.ts